import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Container, AppBar, Toolbar, Typography, Button, makeStyles, TextField, List, ListItem, ListItemText, Divider, Dialog, DialogContent, DialogActions, Box, useMediaQuery, useTheme, ListItemAvatar, Avatar } from '@material-ui/core'
import { UserIdha, ObjectID } from "@idha/idha-types";

export class UserExchangeProps {
    open?: boolean
    users?: UserIdha[]
    getUserPhotoUrl?: (user: UserIdha) => string
    onConclude?: (user: UserIdha, password: string, event: any) => void
    onCancel?: () => void
}


const useStyles = makeStyles(theme => ({
    appBar: {
        position: 'relative'
    },
    "found-0-users": {
        color: "red"
    },
    "found-1-users": {
        color: "green"
    },
    capslockActive: {
        color: "red"
    }
}));



const TopBar = () => {
    const classes = useStyles({});
    const { t, i18n } = useTranslation("UserExchange");

    return (<AppBar className={classes.appBar} elevation={0}>
        <Toolbar>
            <Typography variant="h6">
                {t('title')}
            </Typography>
        </Toolbar>
    </AppBar>)
}

const Search = (props: { value: string, setValue: any, onEnterPressed: any }) => {
    const { t, i18n } = useTranslation("UserExchange");

    const handleKeyPress = function (event) {
        if (event.key == 'Enter') {
            props.onEnterPressed()
        }
    }

    return (
        <TextField
            autoFocus
            key="searchField"
            label={t('search.label')}
            placeholder={t('search.placeholder')}
            fullWidth
            margin="normal"
            InputLabelProps={{
                shrink: true
            }}
            onKeyPress={handleKeyPress}
            variant="standard"
            value={props.value}
            onChange={e => props.setValue(e.target.value)}
        />
    )
}

const UserListItem = (props: { user: UserIdha, avatar?: string, selected?: boolean, showDivider?: boolean, onSelect?: (user: UserIdha) => void }) => {
    let showButton: any = (props.onSelect !== undefined) ? true : false;
    let divider = props.showDivider ? <Divider></Divider> : '';
    return (
        <>
            <ListItem selected={props.selected} button={showButton} onClick={() => props.onSelect?.(props.user)}>
                <ListItemAvatar>
                    <Avatar alt={props.user.name} src={props.avatar} />
                </ListItemAvatar>
                <ListItemText primary={props.user.name} secondary={props.user.email}></ListItemText>
            </ListItem>
            {divider}
        </>
    )
}

const UserList = (props: { users: any, onSelectUser: (user: UserIdha) => void, getUserPhotoUrl: (user: UserIdha) => string }) => {

    const classes = useStyles({});
    const { t, i18n } = useTranslation("UserExchange");


    //Estados
    const [filter, setFilter] = useState("");
    const users: UserIdha[] = Object.values(props.users)

    const filteredUsers: UserIdha[] = useMemo(() => {
        return users.filter(
            user => (user.name?.toLowerCase?.().startsWith?.(filter) ||
                user.email?.toLowerCase?.().startsWith?.(filter) ||
                user.cpf?.toLowerCase?.().startsWith?.(filter))
        )
    }, [props.users, filter])
    //Sugere o usuário, caso ele seja o único retorno para o filtro
    let sugestedUser: UserIdha = ((filteredUsers?.length || 0) == 1) ? filteredUsers[0] : undefined;

    function handleEnterPress() {
        if (sugestedUser) {
            props.onSelectUser(sugestedUser)
        }
    }

    return (
        <>
            <Search key="search" value={filter} setValue={setFilter} onEnterPressed={handleEnterPress} />

            <Typography variant="subtitle2"
                className={classes[`found-${filteredUsers.length}-users`]} >
                {t('result', { postProcess: 'interval', count: filteredUsers.length })}
            </Typography>

            <List>
                {filteredUsers?.map((item: UserIdha) => {
                    let avatar: string = props.getUserPhotoUrl?.(item);
                    return <UserListItem key={item.id.toString()} onSelect={props.onSelectUser} user={item} avatar={avatar} selected={item?.id == sugestedUser?.id}></UserListItem>
                })}
            </List>
        </>
    )
}

const PasswordForm = (props: { user: UserIdha, password: string, onPasswordChange: any, getUserPhotoUrl?: (user: UserIdha) => string }) => {
    const { t, i18n } = useTranslation(["UserExchange", "common"]);
    const [capsLockOn, setCapsLockOn] = useState(false);

    let classes = useStyles({});

    function checkCapsLock(e) {
        let capslockState = e.getModifierState('CapsLock');

        //WorkAround....aparentemente há um delay ao pressionar a tecla CapsLock para desativar,
        //então nesses casos o getModifiedState retorna que o CapsLock ainda está ativo
        if (capsLockOn && capslockState && (e.key == 'CapsLock')) {
            capslockState = false;
        }
        setCapsLockOn(capslockState);
    }

    return (
        <>
            <UserListItem user={props.user} avatar={props.getUserPhotoUrl?.(props.user)}></UserListItem>
            <TextField
                autoFocus
                key="passwordField"
                type="password"
                label={t('password.label')}
                placeholder={t('password.placeholder')}
                fullWidth
                margin="normal"
                InputLabelProps={{
                    shrink: true
                }}
                variant="standard"
                value={props.password}
                onKeyUp={checkCapsLock}
                //onMouseDown={checkCapsLock}
                onChange={e => props.onPasswordChange(e.target.value)}
            />
            {capsLockOn ? <Typography variant="subtitle2"
                className={classes.capslockActive} >
                {t('password.capslock_active')}
            </Typography> : ''}
        </>
    )
}



/**
 * Componente principal: Troca de Usuários
 * Exibe uma lista de usuários, com um filtro de texto.
 * Ao filtrar e selecionar um usuário, solicita a senha
 */
export const UserExchange = (props: UserExchangeProps) => {
    const { t, i18n } = useTranslation("UserExchange");
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));

    //Estados    
    const [selectedUser, setSelectedUser] = useState<UserIdha>(undefined);
    const [password, setPassword] = useState<string>("");

    useEffect(() => {
        if (!props.open) {
            setSelectedUser(undefined);
        }
    }, [props.open])

    useEffect(() => {
        if (!selectedUser) {
            setPassword("");
        }
    }, [selectedUser])

    const handleSelectUser = function (user: UserIdha) {
        setSelectedUser(user);
    }

    const handleCancel = function (event, reason?) {
        if (reason !== 'backdropClick') {
            if (selectedUser) {
                setSelectedUser(undefined)
            } else {
                props.onCancel()
            }
        }
    }

    const handleSubmit = function (event: any) {
        event.preventDefault();
        props.onConclude(selectedUser, password, event)
    }


    let content;
    let displayConfirm;
    if (!selectedUser) {
        content = <UserList users={{ ...props.users }} onSelectUser={handleSelectUser} getUserPhotoUrl={props.getUserPhotoUrl} />;
        displayConfirm = 'none';
    } else {
        content = <PasswordForm user={selectedUser} password={password} onPasswordChange={setPassword} getUserPhotoUrl={props.getUserPhotoUrl} />;
        displayConfirm = 'initial';
    }

    return (
        <>
            <Dialog open={(props.open || false)} maxWidth="sm" fullWidth={true} fullScreen={isSmallScreen} onClose={handleCancel}>
                <TopBar />

                <DialogContent>
                    <form id="userExchageForm" onSubmit={handleSubmit}>
                        {content}
                    </form>
                </DialogContent>

                <Divider></Divider>

                <DialogActions style={{ display: "flex", justifyContent: 'flex-end' }}>
                    <Button onClick={handleCancel}>{t('cancel')}</Button>
                    <Button color="primary" disabled={password?.length <= 0} type="submit" form="userExchageForm" style={{ display: displayConfirm }}>{t('confirm')}</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
